import type { SolutionPageContent } from '../../types/solution'

export const manageInventorySolution: SolutionPageContent = {
  slug: 'manage-inventory',
  hero: {
    headline: 'Always know what’s on your shelves',
    subheadline: 'Track stock as you sell, get alerts before you run out, and stop tying up cash in slow movers.',
    card: {
      type: 'progress',
      title: 'Stock health',
      badge: { label: '4 low', tone: 'warning' },
      rows: [
        { label: 'In stock', value: '312 products', pct: 88, tone: 'positive' },
        { label: 'Low stock', value: '4 products', pct: 8, tone: 'warning' },
        { label: 'Not sold in 60 days', value: 'RWF 420,000', pct: 14, tone: 'negative' },
      ],
      action: 'Review low stock',
    },
  },
  challenges: [
    {
      quote: 'Stock goes missing and I can’t tell where.',
      detail: 'The numbers in my notebook never match what’s on the shelf.',
    },
    {
      quote: 'I always reorder too late — or too much.',
      detail: 'Either the shelf is empty, or cash is stuck in boxes in the back.',
    },
    {
      quote: 'Counting stock takes a whole day.',
      detail: 'We close the shop to count, and the result is out of date by Monday.',
    },
  ],
  helps: [
    {
      solves: 'Stock goes missing',
      title: 'Stock that updates with every sale',
      copy: 'Every sale, purchase, and adjustment is recorded with who did it and when. Differences show up right away, not months later.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Stock movements · Cooking oil 1L',
          rows: [
            { label: 'Sale · Receipt #1842', sublabel: 'Aline · 10:14', value: '− 2', tone: 'default' },
            { label: 'Purchase · PO-0418', sublabel: 'Eric · 09:02', value: '+ 48', tone: 'positive' },
            { label: 'Adjustment · Damaged', sublabel: 'Eric · 08:40', value: '− 1', tone: 'negative' },
          ],
          footer: { label: 'On hand', value: '63' },
        },
      },
    },
    {
      solves: 'I always reorder too late — or too much',
      title: 'Low-stock alerts and smart reordering',
      copy: 'Set reorder levels for each product. Kayko alerts you in time and suggests how much to order based on what you sell.',
      product: 'purchases',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Suggested reorder',
          badge: { label: 'Based on 30 days', tone: 'brand' },
          rows: [
            { label: 'Sugar 1kg', sublabel: 'Sells 12/day · 4 left', value: 'Order 60' },
            { label: 'Rice 5kg', sublabel: 'Sells 5/day · 9 left', value: 'Order 30' },
            { label: 'Soap bar', sublabel: 'Sells 8/day · 22 left', value: 'Order 40' },
          ],
          action: 'Create purchase order',
        },
      },
    },
    {
      solves: 'Counting stock takes a whole day',
      title: 'Quick stock counts on your phone',
      copy: 'Count one shelf at a time with your phone’s camera or a scanner, without closing the shop. Kayko shows differences instantly.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Stock count · Aisle 2',
          badge: { label: 'In progress', tone: 'brand' },
          rows: [
            { label: 'Counted', value: '46 of 58', pct: 79, tone: 'brand' },
            { label: 'Matches', value: '43', pct: 74, tone: 'positive' },
            { label: 'Differences', value: '3', pct: 5, tone: 'warning' },
          ],
          action: 'Finish count',
        },
      },
    },
  ],
  journey: {
    heading: 'How it works',
    subheading: 'Get your stock under control in four steps.',
    items: [
      { title: 'Add your products', copy: 'Import your list with quantities, costs, and prices.' },
      { title: 'Set reorder levels', copy: 'Tell Kayko when each product counts as low.' },
      { title: 'Sell and receive stock', copy: 'Sales and deliveries update stock automatically.' },
      { title: 'Reorder on time', copy: 'Act on alerts with a purchase order in a few taps.' },
    ],
  },
  results: [
    { value: '−60%', label: 'fewer stock-outs on best sellers' },
    { value: '−25%', label: 'less cash tied up in slow-moving stock' },
    { value: '2 hrs', label: 'for a full stock count instead of a day' },
  ],
  testimonial: {
    quote: 'Kayko tells us which medicines are running low and which are about to expire. We order the right amount, every time.',
    name: 'Alice Uwamahoro',
    role: 'Pharmacist & Owner',
    business: 'Ubuzima Pharmacy',
    location: 'Nyamirambo, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/290df7a2-5654-48ae-b59a-6a81a29ab1f5.jpg',
    metric: { value: '−25%', label: 'less cash tied up in stock' },
  },
  products: ['inventory', 'purchases', 'reports'],
  plan: { name: 'Starter', audience: 'businesses managing stock' },
  faq: [
    {
      question: 'Can I import my existing product list?',
      answer: 'Yes. Upload a spreadsheet with names, quantities, costs, and prices, or our Kigali team can help during onboarding.',
    },
    {
      question: 'Do low-stock alerts come by SMS?',
      answer: 'Alerts show in the app on every plan. SMS alerts to chosen staff are available as part of September Early Access.',
    },
    {
      question: 'Can I track stock across several shops or a warehouse?',
      answer: 'Yes. On the Growth plan, keep separate stock per location and move stock between them.',
    },
    {
      question: 'Can I use barcodes?',
      answer: 'Yes. Scan with a USB or Bluetooth scanner, or use your phone’s camera.',
    },
    {
      question: 'Can I see which products don’t sell?',
      answer: 'Yes. Reports show slow movers and stock that hasn’t sold in a set number of days.',
    },
  ],
  related: ['retail', 'healthcare', 'manage-multiple-locations'],
  cta: {
    headline: 'Take control of your stock today',
    subheadline: 'Import your products in minutes. Local support in Kigali when you need it.',
  },
}
